import React from 'react';
import { Collapse } from '@mui/material';

import { TableColumns } from '@models/column';
import { FeatureLeaderboard } from '@models/feature-leaderboard';
import { FeatureId } from '@models/feature';

import { FeatureLeaderboardEntryDetails } from './FeatureLeaderboardEntryDetails';
import { FeatureLeaderboardEntryFeatureEditor } from './FeatureLeaderboardEntryFeatureEditor';
import {
  FeatureActionsProps,
  FeatureLeaderboardEntryDrawerHeader,
} from './feature-leaderboard-entry-drawer-header';

export enum DrawerMode {
  None = 'none',
  Edit = 'edit',
  Copy = 'copy',
  Code = 'code',
  Details = 'details',
}

interface FeatureLeaderboardEntryDrawerProps extends FeatureActionsProps {
  isWithAssessment: boolean;
  featureEntry: FeatureLeaderboard.Entry;
  originFeatureId: FeatureId;
  tableColumn: TableColumns;
  initMode?: DrawerMode;
  onModelChange?: (mode: DrawerMode) => boolean | void; // if false, then do not set inline mode
  onCancel?: () => void;
}

export const FeatureLeaderboardEntryDrawer = (
  props: FeatureLeaderboardEntryDrawerProps
) => {
  const [mode, setMode] = React.useState(props.initMode ?? DrawerMode.None);
  const isExpanded = mode === DrawerMode.Code || mode === DrawerMode.Details;

  const onModeChange = (mode: DrawerMode) => {
    if (props?.onModelChange) {
      const setInlineMode = props.onModelChange(mode) ?? true;
      if (setInlineMode) {
        setMode(mode);
      }
    } else {
      setMode(mode);
    }
  };

  const onCancel = () => {
    setMode(DrawerMode.None);
    props.onCancel?.();
  };

  return (
    <div
      className={`feature-leaderboard-entry-drawer ${
        isExpanded ? 'expanded' : ''
      }`}
    >
      <FeatureLeaderboardEntryDrawerHeader
        mode={mode}
        onModeChange={onModeChange}
        onCancel={onCancel}
        {...props}
      />

      <div className="feature-leaderboard-entry-drawer-details">
        <Collapse
          in={mode === DrawerMode.Code || mode === DrawerMode.Copy}
          unmountOnExit
        >
          <br />
          <FeatureLeaderboardEntryFeatureEditor
            mode={mode}
            featureId={props.featureEntry.feature.id}
            originFeatureId={props.originFeatureId}
            featureDescriptor={props.featureEntry.featureDescriptor}
            tableColumns={props.tableColumn}
            onClose={onCancel}
          />
        </Collapse>
        <Collapse in={mode === DrawerMode.Details} unmountOnExit>
          <FeatureLeaderboardEntryDetails entry={props.featureEntry} />
        </Collapse>
      </div>
    </div>
  );
};
